<template>
    <transition name="debug-bar-slide">
        <div v-if="visible" class="debug-bar-wrapper">
            <!-- Bot Reply Bubble (appears above the card when completed) -->
            <transition name="reply-fade">
                <div v-if="lastReply" class="debug-reply-bubble">
                    <div class="reply-header">
                        <v-icon size="14" color="#888">mdi-robot-outline</v-icon>
                        <span class="reply-label">Bot Reply</span>
                        <span v-if="streaming" class="reply-streaming-label">streaming…</span>
                        <v-tooltip location="top">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props" icon size="x-small" variant="text" class="reply-action-btn" @click="copyReply">
                                    <v-icon size="14">{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ copied ? 'Copied!' : 'Copy' }}</span>
                        </v-tooltip>
                        <v-tooltip location="top">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props" icon size="x-small" variant="text" class="reply-action-btn" @click="replyExpanded = !replyExpanded">
                                    <v-icon size="14">{{ replyExpanded ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ replyExpanded ? 'Collapse' : 'Expand' }}</span>
                        </v-tooltip>
                    </div>
                    <div class="reply-text" :class="{ 'reply-expanded': replyExpanded }">{{ lastReply }}<span v-if="streaming" class="reply-cursor">▍</span></div>
                </div>
            </transition>

            <!-- Main Debug Card — Two-zone layout -->
            <div class="debug-card" :class="{ 'card-running': isRunning }">
                <!-- Zone 1: Composer -->
                <div class="composer" :class="{ 'composer-disabled': isRunning || isPaused }">
                    <button
                        type="button"
                        class="proactive-toggle"
                        :class="{ 'proactive-toggle-active': localProactive }"
                        :disabled="isRunning || isPaused"
                        @click="proactiveExpanded = !proactiveExpanded"
                    >
                        <v-icon size="13">{{ proactiveExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                        <span>Proactive message (ContinueQuestion)</span>
                        <span v-if="localProactive && !proactiveExpanded" class="proactive-dot"></span>
                    </button>
                    <transition name="proactive-collapse">
                        <textarea
                            v-if="proactiveExpanded"
                            v-model="localProactive"
                            placeholder="Proactive message (ContinueQuestion) — optional"
                            :disabled="isRunning || isPaused"
                            class="composer-textarea proactive-textarea"
                            rows="2"
                        ></textarea>
                    </transition>
                    <textarea
                        ref="textarea"
                        v-model="localMessage"
                        :placeholder="inputPlaceholder"
                        :disabled="isRunning || isPaused"
                        class="composer-textarea"
                        rows="1"
                        @input="autoGrow"
                        @keydown="handleKeydown"
                        @focus="textareaFocused = true"
                        @blur="textareaFocused = false"
                    ></textarea>
                    <div class="composer-footer">
                        <label class="keep-memory-toggle" :class="{ 'toggle-disabled': isRunning || isPaused }">
                            <input
                                type="checkbox"
                                class="keep-memory-cb"
                                :checked="keepMemory"
                                :disabled="isRunning || isPaused"
                                @change="$emit('update:keep-memory', $event.target.checked)"
                            />
                            <span class="toggle-label">Keep Memory</span>
                        </label>
                        <label class="keep-memory-toggle voice-toggle" :class="{ 'toggle-disabled': isRunning || isPaused }">
                            <input
                                type="checkbox"
                                class="keep-memory-cb voice-cb"
                                :checked="voiceMode"
                                :disabled="isRunning || isPaused"
                                @change="$emit('update:voice-mode', $event.target.checked)"
                            />
                            <span class="toggle-label">Voice</span>
                        </label>
                        <select
                            v-if="voiceMode"
                            class="voice-lang-select"
                            :value="voiceLang"
                            :disabled="isRunning || isPaused"
                            @change="$emit('update:voice-lang', $event.target.value)"
                        >
                            <option value="th">ไทย</option>
                            <option value="en">English</option>
                            <option value="zh">中文</option>
                        </select>
                        <label class="keep-memory-toggle cif-toggle" :class="{ 'toggle-disabled': isRunning || isPaused }" title="ใช้ CIF แทน userId (จำลองลูกค้าด้วยเลข CIF)">
                            <input
                                type="checkbox"
                                class="keep-memory-cb cif-cb"
                                data-testid="debug-use-cif-cb"
                                :checked="useCif"
                                :disabled="isRunning || isPaused"
                                @change="$emit('update:useCif', $event.target.checked)"
                            />
                            <span class="toggle-label">ใช้ CIF</span>
                        </label>
                        <label v-if="useCif" class="cif-field" title="จำลอง CIF สำหรับ debug — ใช้แทน userId">
                            <span class="cif-label">CIF</span>
                            <input
                                type="text"
                                class="cif-input"
                                data-testid="debug-cif-input"
                                :value="debugCif"
                                :disabled="isRunning || isPaused"
                                @input="$emit('update:debugCif', $event.target.value)"
                                placeholder="เช่น 1234567890"
                                inputmode="numeric"
                                autocomplete="off"
                            />
                        </label>
                        <v-tooltip v-if="!useCif && debugUserId" location="top">
                            <template v-slot:activator="{ props }">
                                <span v-bind="props" class="debug-uid" @click.stop="copyUserId">
                                    {{ debugUserId }}
                                    <v-icon size="10" class="uid-copy-icon">{{ uidCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                </span>
                            </template>
                            <span>{{ uidCopied ? 'Copied!' : 'Click to copy User ID' }}</span>
                        </v-tooltip>
                        <span class="composer-spacer"></span>
                        <transition name="hint-fade">
                            <span v-if="showKeyboardHint" class="keyboard-hint">
                                <kbd>↵</kbd> send &middot; <kbd>⇧↵</kbd> new line
                            </span>
                        </transition>
                        <button
                            class="send-btn"
                            :class="{ 'send-active': canSend }"
                            :disabled="!canSend"
                            @click="handleSend"
                        >
                            <v-icon size="16">mdi-send</v-icon>
                        </button>
                    </div>
                </div>

                <!-- Zone 2: Status Strip -->
                <div class="status-strip">
                    <!-- Running progress bar -->
                    <div v-if="isRunning" class="strip-progress"></div>

                    <div class="strip-left">
                        <span class="status-dot" :class="statusDotClass">
                            <v-icon v-if="isRunning" size="8" class="dot-spin">mdi-loading</v-icon>
                        </span>
                        <span class="strip-label">{{ statusText }}</span>
                    </div>

                    <div class="strip-right">
                        <!-- Info badges (left side of right zone) -->
                        <div class="strip-badges" v-if="breakpointCount > 0 || stepCount > 0 || setNextTargetName">
                            <!-- Set Next Statement indicator -->
                            <span v-if="setNextTargetName" class="mini-badge badge-setnext" @click="$emit('cancel-set-next')" title="Cancel Set Next Statement">
                                <v-icon size="10" class="mr-1">mdi-arrow-right-bold</v-icon>
                                {{ setNextTargetName }}
                                <v-icon size="10" class="ml-1 cancel-x">mdi-close</v-icon>
                            </span>
                            <span v-if="breakpointCount > 0" class="mini-badge badge-bp">
                                <span class="bp-dot"></span>
                                {{ breakpointCount }}
                            </span>
                            <span v-if="stepCount > 0" class="mini-badge badge-steps">
                                {{ stepCount }} step{{ stepCount > 1 ? 's' : '' }}
                            </span>
                        </div>

                        <!-- Playback controls (always far right) -->
                        <span class="strip-sep" v-if="breakpointCount > 0 || stepCount > 0 || setNextTargetName"></span>
                        <div class="strip-controls">
                            <v-tooltip location="top">
                                <template v-slot:activator="{ props }">
                                    <button
                                        v-bind="props"
                                        class="ctrl-btn ctrl-resume"
                                        :class="{ 'ctrl-enabled': isPaused }"
                                        :disabled="!isPaused"
                                        @click="$emit('resume')"
                                    >
                                        <v-icon size="14">mdi-play</v-icon>
                                    </button>
                                </template>
                                <span>Resume (continue to next breakpoint)</span>
                            </v-tooltip>
                            <v-tooltip location="top">
                                <template v-slot:activator="{ props }">
                                    <button
                                        v-bind="props"
                                        class="ctrl-btn ctrl-stop"
                                        :class="{ 'ctrl-enabled': isRunning || isPaused }"
                                        :disabled="!isRunning && !isPaused"
                                        @click="$emit('stop')"
                                    >
                                        <v-icon size="14">mdi-stop</v-icon>
                                    </button>
                                </template>
                                <span>Stop execution</span>
                            </v-tooltip>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'DebugControlBar',
    props: {
        visible: { type: Boolean, default: false },
        status: { type: String, default: 'ready' }, // ready, running, paused, completed, error, stopped
        pausedNodeName: { type: String, default: '' },
        breakpointCount: { type: Number, default: 0 },
        stepCount: { type: Number, default: 0 },
        totalDuration: { type: Number, default: 0 },
        errorMessage: { type: String, default: '' },
        message: { type: String, default: '' },
        lastReply: { type: String, default: '' },
        streaming: { type: Boolean, default: false },
        setNextTargetName: { type: String, default: '' },
        keepMemory: { type: Boolean, default: false },
        voiceMode: { type: Boolean, default: false },
        voiceLang: { type: String, default: 'th' },
        proactiveMessage: { type: String, default: '' },
        debugUserId: { type: String, default: '' },
        debugCif: { type: String, default: '' },
        useCif: { type: Boolean, default: false },
    },
    emits: ['send', 'resume', 'stop', 'update:message', 'cancel-set-next', 'update:keep-memory', 'update:voice-mode', 'update:voice-lang', 'update:proactive-message', 'update:debugCif', 'update:useCif'],
    data() {
        return {
            localMessage: this.message,
            localProactive: this.proactiveMessage,
            copied: false,
            uidCopied: false,
            replyExpanded: false,
            textareaFocused: false,
            proactiveExpanded: false,
        };
    },
    watch: {
        message(val) { this.localMessage = val; },
        proactiveMessage(val) { this.localProactive = val; },
        localProactive(val) { this.$emit('update:proactive-message', val); },
        localMessage(val) {
            this.$emit('update:message', val);
            this.$nextTick(() => this.autoGrow());
        },
        lastReply() { this.replyExpanded = false; },
        visible(val) {
            if (val) this.$nextTick(() => this.autoGrow());
        },
    },
    computed: {
        isRunning() { return this.status === 'running'; },
        isPaused() { return this.status === 'paused'; },
        canSend() { return this.localMessage?.trim() && !this.isRunning && !this.isPaused; },
        showKeyboardHint() {
            return this.textareaFocused && this.localMessage && !this.isRunning && !this.isPaused;
        },
        inputPlaceholder() {
            if (this.isRunning) return 'Flow is executing…';
            if (this.isPaused) return `⏸ Paused at "${this.pausedNodeName || 'breakpoint'}" — press Resume to continue`;
            return 'Type a test message… (Enter to send, Shift+Enter for new line)';
        },
        shortUserId() {
            if (!this.debugUserId) return '';
            return this.debugUserId.substring(0, 8) + '…';
        },
        statusDotClass() {
            return {
                'dot-ready': this.status === 'ready',
                'dot-running': this.status === 'running',
                'dot-paused': this.status === 'paused',
                'dot-completed': this.status === 'completed',
                'dot-error': this.status === 'error' || this.status === 'stopped',
            };
        },
        statusText() {
            if (this.status === 'ready') return 'Ready to debug';
            if (this.status === 'running') return `Running step ${this.stepCount}…`;
            if (this.status === 'paused') return `Paused · ${this.pausedNodeName || 'breakpoint'}`;
            if (this.status === 'completed') return `Done ${this.formatDuration(this.totalDuration)}`;
            if (this.status === 'error') return this.errorMessage || 'Error';
            if (this.status === 'stopped') return 'Stopped';
            return '';
        },
    },
    methods: {
        handleKeydown(e) {
            // Enter without Shift → send
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.handleSend();
            }
            // Shift+Enter → allow default (newline)
        },
        handleSend() {
            if (this.canSend) {
                this.$emit('send', this.localMessage.trim());
                // Reset textarea height after send
                this.$nextTick(() => {
                    if (this.$refs.textarea) {
                        this.$refs.textarea.style.height = 'auto';
                    }
                });
            }
        },
        autoGrow() {
            const el = this.$refs.textarea;
            if (!el) return;
            el.style.height = 'auto';
            const maxHeight = 140; // ~6 rows
            el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
            // Enable scroll if content exceeds max
            el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
        },
        formatDuration(ms) {
            if (!ms) return '0ms';
            if (ms < 1000) return `${ms}ms`;
            return `${(ms / 1000).toFixed(1)}s`;
        },
        async copyReply() {
            try {
                await navigator.clipboard.writeText(this.lastReply);
                this.copied = true;
                setTimeout(() => { this.copied = false; }, 1500);
            } catch { /* fallback */ }
        },
        async copyUserId() {
            try {
                await navigator.clipboard.writeText(this.debugUserId);
                this.uidCopied = true;
                setTimeout(() => { this.uidCopied = false; }, 1500);
            } catch { /* fallback */ }
        },
    },
};
</script>

<style scoped>
/* ===== Wrapper ===== */
.debug-bar-wrapper {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 600px;
    max-width: calc(100vw - 32px);
}

/* ===== Reply Bubble ===== */
.debug-reply-bubble {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.96);
    backdrop-filter: blur(16px);
    border-radius: 14px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    width: 100%;
}

.reply-header {
    display: flex;
    align-items: center;
    gap: 6px;
}

.reply-label {
    font-size: 11px;
    font-weight: 600;
    color: #888;
    flex: 1;
}

.reply-streaming-label {
    font-size: 10px;
    font-weight: 600;
    color: #1976d2;
    margin-right: 6px;
    animation: reply-streaming-pulse 1.2s ease-in-out infinite;
}

@keyframes reply-streaming-pulse {
    0%, 100% { opacity: 0.45; }
    50% { opacity: 1; }
}

.reply-cursor {
    display: inline-block;
    color: #1976d2;
    font-weight: 700;
    animation: reply-cursor-blink 1s step-end infinite;
}

@keyframes reply-cursor-blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
}

.reply-text {
    font-size: 13px;
    color: #333;
    line-height: 1.45;
    word-break: break-word;
    white-space: pre-wrap;
    max-height: calc(1.45em * 5);
    overflow-y: auto;
    padding-left: 20px;
    transition: max-height 0.3s ease;
}

.reply-expanded {
    max-height: 50vh;
}

.reply-text::-webkit-scrollbar { width: 4px; }
.reply-text::-webkit-scrollbar-thumb { background: #ddd; border-radius: 2px; }

.reply-action-btn {
    flex-shrink: 0;
    color: #aaa !important;
}
.reply-action-btn:hover { color: #555 !important; }

.reply-fade-enter-active { transition: all 0.3s ease; }
.reply-fade-leave-active { transition: all 0.2s ease; }
.reply-fade-enter-from { opacity: 0; transform: translateY(8px); }
.reply-fade-leave-to { opacity: 0; transform: translateY(8px); }

/* ===== Main Debug Card ===== */
.debug-card {
    width: 100%;
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: blur(20px);
    border-radius: 16px;
    box-shadow:
        0 4px 24px rgba(0, 0, 0, 0.10),
        0 0 0 1px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    transition: box-shadow 0.3s ease;
}

.debug-card.card-running {
    box-shadow:
        0 4px 24px rgba(33, 150, 243, 0.12),
        0 0 0 1px rgba(33, 150, 243, 0.15);
}

/* ===== Zone 1: Composer ===== */
.composer {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 12px 14px 6px 14px;
    gap: 4px;
}

.composer-disabled {
    opacity: 0.6;
    pointer-events: none;
}

.composer-textarea {
    width: 100%;
    min-height: 36px;
    max-height: 140px;
    padding: 8px 12px;
    padding-right: 44px; /* space for send button */
    border: 1.5px solid #e5e7eb;
    border-radius: 12px;
    background: #fafbfc;
    color: #1a1a1a;
    font-size: 13px;
    font-family: inherit;
    line-height: 1.5;
    resize: none;
    outline: none;
    overflow-y: hidden;
    transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
}

/* Proactive (ContinueQuestion) collapse toggle */
.proactive-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    align-self: flex-start;
    padding: 2px 6px 2px 2px;
    margin-bottom: 2px;
    border: none;
    background: transparent;
    color: #9a8fb5;
    font-size: 11.5px;
    font-weight: 500;
    cursor: pointer;
    border-radius: 6px;
    transition: color 0.15s ease, background 0.15s ease;
}
.proactive-toggle:hover {
    background: rgba(124, 58, 237, 0.06);
    color: #7c3aed;
}
.proactive-toggle:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
.proactive-toggle-active {
    color: #7c3aed;
}
.proactive-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #7c3aed;
}

.proactive-collapse-enter-active,
.proactive-collapse-leave-active {
    transition: opacity 0.2s ease, max-height 0.25s ease;
    overflow: hidden;
    max-height: 80px;
}
.proactive-collapse-enter-from,
.proactive-collapse-leave-to {
    opacity: 0;
    max-height: 0;
}

/* Proactive (ContinueQuestion) input — visually subordinate to main composer */
.proactive-textarea {
    min-height: 32px;
    max-height: 80px;
    padding-right: 12px;
    font-size: 12px;
    background: #f6f4fb;
    border-color: #e4def2;
    border-style: dashed;
    margin-bottom: 4px;
}
.proactive-textarea:focus {
    border-style: solid;
    border-color: #b9a6e6;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08);
}

.composer-textarea:focus {
    border-color: #93bbec;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.08);
}

.composer-textarea::placeholder {
    color: #b0b7c3;
    font-size: 12.5px;
}

.composer-textarea:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
    color: #999;
}

.composer-textarea::-webkit-scrollbar { width: 4px; }
.composer-textarea::-webkit-scrollbar-thumb { background: #d4d4d4; border-radius: 2px; }

/* Footer inside composer (hint + send btn) */
.composer-footer {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-height: 28px;
    padding: 0 2px;
}

.composer-spacer {
    flex: 1;
}

/* Keep Memory toggle */
.keep-memory-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
    padding: 2px 6px;
    border-radius: 6px;
    transition: background 0.15s ease;
}
.keep-memory-toggle:hover {
    background: rgba(124, 58, 237, 0.06);
}
.keep-memory-toggle.toggle-disabled {
    opacity: 0.45;
    pointer-events: none;
}
.keep-memory-cb {
    width: 13px;
    height: 13px;
    accent-color: #7c3aed;
    cursor: pointer;
    margin: 0;
}
.toggle-label {
    font-size: 11px;
    font-weight: 500;
    color: #888;
    white-space: nowrap;
}
.voice-toggle:hover {
    background: rgba(25, 118, 210, 0.06);
}
.voice-cb {
    accent-color: #1976d2;
}
.voice-lang-select {
    font-size: 11px;
    font-weight: 500;
    color: #555;
    background: #fff;
    border: 1px solid #d7dbe3;
    border-radius: 6px;
    padding: 1px 4px;
    cursor: pointer;
    outline: none;
    transition: border-color 0.15s ease;
}
.voice-lang-select:hover {
    border-color: #1976d2;
}
.voice-lang-select:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
/* Simulated CIF input (mirrors .voice-lang-select / .toggle-label tokens) */
.cif-field {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.cif-label {
    font-size: 11px;
    font-weight: 500;
    color: #888;
    white-space: nowrap;
}
.cif-input {
    font-size: 11px;
    color: #555;
    background: #fff;
    border: 1px solid #d7dbe3;
    border-radius: 6px;
    padding: 1px 6px;
    width: 110px;
    outline: none;
    transition: border-color 0.15s ease;
}
.cif-input:hover,
.cif-input:focus {
    border-color: #1976d2;
}
.cif-input:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
.debug-uid {
    font-size: 10px;
    font-family: 'Cascadia Code', 'Fira Code', monospace;
    color: #aaa;
    background: rgba(0, 0, 0, 0.04);
    padding: 1px 5px;
    border-radius: 4px;
    letter-spacing: 0.3px;
    white-space: nowrap;
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: background 0.15s ease, color 0.15s ease;
}
.debug-uid:hover {
    background: rgba(0, 0, 0, 0.08);
    color: #666;
}
.uid-copy-icon {
    opacity: 0.4;
    transition: opacity 0.15s ease;
}
.debug-uid:hover .uid-copy-icon {
    opacity: 0.8;
}

.keyboard-hint {
    font-size: 10.5px;
    color: #bbb;
    user-select: none;
    white-space: nowrap;
}

.keyboard-hint kbd {
    display: inline-block;
    padding: 0px 4px;
    font-size: 10px;
    font-family: inherit;
    background: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 3px;
    color: #888;
    line-height: 1.5;
}

.hint-fade-enter-active { transition: opacity 0.25s ease; }
.hint-fade-leave-active { transition: opacity 0.15s ease; }
.hint-fade-enter-from,
.hint-fade-leave-to { opacity: 0; }

/* Send button */
.send-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    border: none;
    background: #e2e5ea;
    color: #aaa;
    cursor: not-allowed;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.send-btn.send-active {
    background: #1976d2;
    color: #fff;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.25);
}

.send-btn.send-active:hover {
    background: #1565c0;
    box-shadow: 0 3px 12px rgba(25, 118, 210, 0.35);
    transform: translateY(-1px);
}

.send-btn.send-active:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(25, 118, 210, 0.2);
}

/* ===== Zone 2: Status Strip ===== */
.status-strip {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 14px;
    background: #f7f8fa;
    border-top: 1px solid rgba(0, 0, 0, 0.04);
    min-height: 34px;
    overflow: hidden;
}

/* Animated progress bar for running state */
.strip-progress {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
        90deg,
        transparent 0%,
        #42a5f5 30%,
        #1976d2 50%,
        #42a5f5 70%,
        transparent 100%
    );
    background-size: 200% 100%;
    animation: progress-sweep 1.8s ease-in-out infinite;
}

@keyframes progress-sweep {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
}

/* Strip left: status */
.strip-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    min-width: 0;
}

.status-dot {
    position: relative;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.status-dot.dot-ready { background: #90caf9; }
.status-dot.dot-running {
    background: #66bb6a;
    width: 14px;
    height: 14px;
}
.status-dot.dot-paused { background: #ffb74d; animation: pulse-dot 1.5s ease-in-out infinite; }
.status-dot.dot-completed { background: #66bb6a; }
.status-dot.dot-error { background: #ef5350; }

.dot-spin {
    color: #fff;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@keyframes pulse-dot {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
}

.strip-label {
    font-size: 11.5px;
    font-weight: 500;
    color: #666;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 220px;
}

/* Strip right: controls + badges */
.strip-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.strip-controls {
    display: flex;
    align-items: center;
    gap: 2px;
}

/* Control buttons (ghost style) */
.ctrl-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    border: none;
    background: transparent;
    color: #ccc;
    cursor: not-allowed;
    transition: all 0.15s ease;
}

.ctrl-btn.ctrl-enabled {
    cursor: pointer;
}

.ctrl-resume.ctrl-enabled {
    color: #43a047;
    background: rgba(67, 160, 71, 0.08);
}
.ctrl-resume.ctrl-enabled:hover {
    background: rgba(67, 160, 71, 0.18);
    color: #2e7d32;
}

.ctrl-stop.ctrl-enabled {
    color: #e53935;
    background: rgba(229, 57, 53, 0.08);
}
.ctrl-stop.ctrl-enabled:hover {
    background: rgba(229, 57, 53, 0.18);
    color: #c62828;
}

/* Separator between controls and badges */
.strip-sep {
    width: 1px;
    height: 16px;
    background: #e0e0e0;
    flex-shrink: 0;
}

/* Badge section */
.strip-badges {
    display: flex;
    align-items: center;
    gap: 6px;
}

.mini-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10.5px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    white-space: nowrap;
    line-height: 1;
}

.badge-bp {
    background: rgba(229, 57, 53, 0.08);
    color: #d32f2f;
}

.bp-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ef5350;
}

.badge-steps {
    background: rgba(25, 118, 210, 0.07);
    color: #1565c0;
}

.badge-setnext {
    background: rgba(245, 158, 11, 0.12);
    color: #92400e;
    cursor: pointer;
    transition: background 0.15s ease;
}
.badge-setnext:hover {
    background: rgba(245, 158, 11, 0.25);
}
.badge-setnext .cancel-x {
    opacity: 0.5;
    transition: opacity 0.15s ease;
}
.badge-setnext:hover .cancel-x {
    opacity: 1;
}

/* ===== Slide Transition ===== */
.debug-bar-slide-enter-active,
.debug-bar-slide-leave-active {
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.debug-bar-slide-enter-from,
.debug-bar-slide-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(24px);
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
    .debug-bar-wrapper {
        width: calc(100vw - 24px);
    }

    .strip-label { max-width: 120px; }

    .keyboard-hint { display: none; }
}
</style>
